import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '../services/userService';
import type { User } from '../types/user.types';
import { useReportError } from '@features/support/SupportErrorProvider';
import { extractErrorInfo } from '@features/support/errorInfo';

export function useUsers(params?: {
  page?: number;
  pageSize?: number;
  search?: string;
  role?: string;
}) {
  // Use a single cache key for all users (only varies by role)
  // This ensures we only fetch once and all filtering/pagination is instant
  return useQuery({
    queryKey: ['users', params?.role || 'all'],
    queryFn: () => userService.getUsers(params),
    staleTime: 2 * 60 * 1000, // 2 minutes - data stays fresh
    gcTime: 10 * 60 * 1000, // 10 minutes cache time
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnReconnect: false, // Don't refetch on reconnect
    // Client-side filtering and pagination happens in the queryFn
    // but React Query will cache the API response and reuse it
  });
}

export function useUser(id: string) {
  return useQuery({
    queryKey: ['user', id],
    queryFn: () => userService.getUserById(id),
    enabled: !!id,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  const reportError = useReportError();

  return useMutation({
    mutationFn: (user: Partial<User> & {
      email: string;
      idNumber: string;
      firstName: string;
      lastName: string;
      roles: string[];
      branchOffices: string[];
    }) => userService.createUser(user),
    onSuccess: async () => {
      // Wait for Aludra to index the new user
      await new Promise(resolve => setTimeout(resolve, 2000));
      // Invalidate and force refetch all user queries
      await queryClient.refetchQueries({ queryKey: ['users'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['consumption'] });
    },
    onError: (error: any) => {
      reportError({ title: 'Error al crear usuario', screen: 'crear usuario', ...extractErrorInfo(error) });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: {
      id: string;
      data: Partial<User> & {
        email: string;
        idNumber: string;
        firstName: string;
        lastName: string;
        roles: string[];
        branchOffices: string[];
        password?: string;
      }
    }) =>
      userService.updateUser(id, data),
    onSuccess: async () => {
      // Wait for Aludra to index the updated user
      await new Promise(resolve => setTimeout(resolve, 1500));
      // Invalidate all user queries (including ['users', 'all'])
      queryClient.invalidateQueries({ queryKey: ['users'], exact: false });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userService.deleteUser(id),
    onSuccess: () => {
      // Invalidate all user queries (including ['users', 'all'])
      queryClient.invalidateQueries({ queryKey: ['users'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['consumption'] });
    },
  });
}

export function useReactivateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userService.reactivateUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['consumption'] });
    },
  });
}

export function useRoles() {
  return useQuery({
    queryKey: ['roles'],
    queryFn: () => userService.getRoles(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useBranchOffices() {
  return useQuery({
    queryKey: ['branch-offices'],
    queryFn: () => userService.getBranchOffices(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
